import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  Mail,
  IdCard,
  HeartPulse,
  Pencil,
  Printer,
  AlertTriangle,
  RefreshCw,
  ClipboardList,
  KeyRound,
  Copy,
  Check,
} from 'lucide-react';
import {
  EPISODE_STATUS_LABELS,
  EPISODE_STATUS_VARIANTS,
  PatientIdSchema,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge, BadgeDot } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { avatarGradient } from '@/components/ui/avatar';
import { usePageTitle } from '@/shared/store/page';
import { ageFromBirthdate, initials, formatShortDateEs } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { useToast } from '@/shared/toast/toast-context';
import { useProceduresList } from '@/features/procedures/api';
import { usePatientById, usePortalCredentials } from './api';

export function PatientDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: patient, isLoading, isError, error, refetch } = usePatientById(id);

  usePageTitle(
    patient !== undefined ? `${patient.nombres} ${patient.apellidos}` : 'Paciente',
    'Expediente único del paciente',
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/pacientes">
              <ArrowLeft className="w-4 h-4" /> Volver
            </Link>
          </Button>
        </div>
        <Card>
          <div className="p-6 flex items-start gap-5" aria-busy="true" aria-live="polite">
            <div className="w-16 h-16 rounded-lg bg-surface-alt animate-pulse" />
            <div className="flex-1 space-y-3">
              <div className="h-6 w-1/3 rounded bg-surface-alt animate-pulse" />
              <div className="h-4 w-1/2 rounded bg-surface-alt animate-pulse" />
              <div className="grid grid-cols-2 gap-4 mt-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-10 rounded bg-surface-alt animate-pulse" />
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (isError || patient === undefined) {
    return (
      <div className="flex flex-col gap-5">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/pacientes">
              <ArrowLeft className="w-4 h-4" /> Volver
            </Link>
          </Button>
        </div>
        <Card>
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft text-danger mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="text-text font-bold text-base mb-1">
              {isError ? 'No fue posible cargar el paciente' : 'Paciente no encontrado'}
            </div>
            <p className="text-text-muted text-sm mb-4">
              {isError
                ? error?.message ?? 'Ocurrió un error inesperado'
                : `El identificador ${id ?? ''} no existe en el registro.`}
            </p>
            <div className="flex justify-center gap-2">
              {isError && (
                <Button variant="secondary" onClick={() => void refetch()}>
                  <RefreshCw className="w-4 h-4" /> Reintentar
                </Button>
              )}
              <Button variant="secondary" asChild>
                <Link to="/pacientes">
                  <ArrowLeft className="w-4 h-4" /> Volver al listado
                </Link>
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const edad = ageFromBirthdate(patient.fechaNacimiento);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/pacientes">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" asChild>
            <Link to={`/pacientes/${patient.id}/imprimir`}>
              <Printer className="w-4 h-4" /> Imprimir historia
            </Link>
          </Button>
          <Button asChild>
            <Link to={`/pacientes/${patient.id}/editar`}>
              <Pencil className="w-4 h-4" /> Editar
            </Link>
          </Button>
        </div>
      </div>

      {/* Resumen del paciente */}
      <Card>
        <div className="p-6 flex items-start gap-5">
          <div
            className={cn(
              'w-16 h-16 rounded-lg grid place-items-center text-white font-extrabold text-lg flex-shrink-0',
              avatarGradient(patient.id),
            )}
          >
            {initials(patient.nombres, patient.apellidos)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-xl font-extrabold text-text">
                {patient.nombres} {patient.apellidos}
              </h2>
              <Badge variant="indigo">{patient.id}</Badge>
              {patient.historiaClinica !== undefined && patient.historiaClinica !== '' && (
                <Badge variant="gray">{patient.historiaClinica}</Badge>
              )}
            </div>
            <div className="text-text-muted text-sm">
              {edad} años ·{' '}
              {patient.sexo === 'F' ? 'Femenino' : patient.sexo === 'M' ? 'Masculino' : 'Otro'} ·
              Nacimiento {formatShortDateEs(patient.fechaNacimiento)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm">
              <InfoRow icon={<IdCard className="w-4 h-4" />} label="Documento">
                {patient.tipoDoc} {patient.documento}
              </InfoRow>
              <InfoRow icon={<Phone className="w-4 h-4" />} label="Teléfono">
                {patient.telefono ?? '—'}
              </InfoRow>
              <InfoRow icon={<Mail className="w-4 h-4" />} label="EPS">
                {patient.eps ?? '—'}
              </InfoRow>
              <InfoRow icon={<HeartPulse className="w-4 h-4" />} label="Alergias">
                {patient.alergias ?? '—'}
              </InfoRow>
              <InfoRow icon={<Phone className="w-4 h-4" />} label="Contacto de emergencia">
                {patient.contactoEmergencia ?? '—'}
              </InfoRow>
            </div>
          </div>
        </div>
      </Card>

      {/* Credenciales del portal de resultados */}
      <PortalCredentialsCard patientId={patient.id} />

      {/* Histórico de procedimientos */}
      <PatientProceduresCard patientId={patient.id} fallbackCount={patient.procedimientosCount} />
    </div>
  );
}

function PortalCredentialsCard({ patientId }: { patientId: string }): JSX.Element {
  const { showToast } = useToast();
  const credsQuery = usePortalCredentials(patientId);
  const [copied, setCopied] = useState(false);

  const documento = credsQuery.data?.usuario ?? null;

  const copy = async (): Promise<void> => {
    if (documento === null) return;
    try {
      await navigator.clipboard.writeText(documento);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast({ kind: 'danger', title: 'No se pudo copiar' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-primary" /> Portal de resultados
          </CardTitle>
          <CardDescription>
            El paciente ingresa con su <strong>número de documento (cédula)</strong> como
            usuario y contraseña.
          </CardDescription>
        </div>
      </CardHeader>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <InfoRow icon={<IdCard className="w-4 h-4" />} label="Usuario">
          {credsQuery.isLoading ? '…' : (documento ?? '—')}
        </InfoRow>
        <div className="flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-text-muted">
            Contraseña
          </div>
          {credsQuery.isLoading ? (
            <div className="h-6 w-32 rounded bg-surface-alt animate-pulse" />
          ) : (
            <div className="flex items-center gap-2">
              <code className="font-mono text-base tracking-wide bg-surface-alt px-2 py-1 rounded">
                {documento ?? '—'}
              </code>
              {documento !== null && (
                <Button variant="ghost" size="sm" onClick={() => void copy()} aria-label="Copiar">
                  {copied ? (
                    <Check className="w-4 h-4 text-success" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

interface PatientProceduresCardProps {
  patientId: string;
  fallbackCount: number;
}

function PatientProceduresCard({
  patientId,
  fallbackCount,
}: PatientProceduresCardProps): JSX.Element {
  const navigate = useNavigate();
  const parsed = PatientIdSchema.safeParse(patientId);
  const validId = parsed.success ? parsed.data : undefined;

  const { data, isLoading, isError, error, refetch } = useProceduresList(
    validId !== undefined
      ? { page: 1, pageSize: 50, pacienteId: validId }
      : { page: 1, pageSize: 1 },
  );

  const items = validId === undefined ? [] : (data?.items ?? []);
  const total = validId === undefined ? fallbackCount : (data?.total ?? 0);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Historial de procedimientos</CardTitle>
          <CardDescription>
            {isLoading
              ? 'Cargando episodios…'
              : `${total} ${total === 1 ? 'episodio registrado' : 'episodios registrados'}`}
          </CardDescription>
        </div>
      </CardHeader>

      {isError ? (
        <div className="p-6 flex items-start gap-3 bg-danger-soft border-t border-red-200 text-red-800 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">No fue posible cargar los procedimientos</div>
            <div>{error.message}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        </div>
      ) : isLoading ? (
        <div className="p-6 space-y-2" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-surface-alt animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-surface-alt grid place-items-center text-text-muted">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div className="text-text font-semibold text-sm">
            Sin procedimientos registrados
          </div>
          <p className="text-text-muted text-sm max-w-md">
            Cuando se cree un episodio clínico para este paciente, aparecerá listado aquí.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Episodio</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Médico</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((ep) => (
              <TableRow
                key={ep.id}
                className="cursor-pointer hover:bg-surface-alt"
                onClick={() => navigate(`/procedimientos/${ep.id}`)}
              >
                <TableCell>
                  <div className="font-mono text-xs text-text-soft">{ep.id}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{formatShortDateEs(ep.fecha)}</div>
                  <div className="text-text-muted text-xs">{ep.hora}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{ep.tipo}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-text">{ep.medicoNombre}</div>
                </TableCell>
                <TableCell>
                  <Badge variant={EPISODE_STATUS_VARIANTS[ep.estado]}>
                    <BadgeDot /> {EPISODE_STATUS_LABELS[ep.estado]}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}

function InfoRow({ icon, label, children }: InfoRowProps): JSX.Element {
  return (
    <div className="flex items-start gap-2.5">
      <div className="text-text-muted mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-text-muted text-[11px] uppercase tracking-wider font-semibold">
          {label}
        </div>
        <div className="text-text text-sm">{children}</div>
      </div>
    </div>
  );
}
